class MicRecorder {
  constructor(config) {
    this.config = {
      // There is a known issue with some macOS machines, where the recording
      // will sometimes have a loud 'pop' or 'pop-click' sound. This flag
      // prevents getting audio from the microphone a few milliseconds after
      // the begining of the recording. It also helps to remove the mouse
      // "click" sound from the output mp3 file.
      startRecordingAt: 300,
      deviceId: null
    };

    this.activeStream = null;
    this.context = null;
    this.microphone = null;
    this.processor = null;

    this.worker = new Worker("./worker.js", { type: "module" });

    Object.assign(this.config, config);
  }

  createProcessor(bufferSize) {
    if (this.context.createJavaScriptNode) {
      return this.context.createJavaScriptNode(bufferSize, 1, 1);
    } else if (this.context.createScriptProcessor) {
      return this.context.createScriptProcessor(bufferSize, 1, 1);
    } else {
      throw "Web audio is not supported.";
    }
  }

  /**
   * Starts to listen for the microphone sound
   * @param {MediaStream} stream
   */
  addMicrophoneListener(stream) {
    this.activeStream = stream;

    // This prevents the weird noise once you start listening to the microphone
    this.timerToStart = setTimeout(() => {
      delete this.timerToStart;
    }, this.config.startRecordingAt);

    // Set up Web Audio API to process data from the media stream (microphone).
    this.microphone = this.context.createMediaStreamSource(stream);
    this.processor = this.createProcessor(0);

    // Add all buffers from LAME into an array.
    this.processor.onaudioprocess = event => {
      if (this.timerToStart) {
        return;
      }

      const b = event.inputBuffer.getChannelData(0);
      this.worker.postMessage(["encode", b]);
    };

    // Begin retrieving microphone data.
    this.microphone.connect(this.processor);
    this.processor.connect(this.context.destination);
  }

  stopAndEncodeToMp3() {
    if (this.processor && this.microphone) {
      // Clean up the Web Audio API resources.
      this.microphone.disconnect();
      this.processor.disconnect();

      this.processor.onaudioprocess = null;

      // Stop all audio tracks. Also, removes recording icon from chrome tab
      this.activeStream.getAudioTracks().forEach(track => track.stop());
    }

    return new Promise((resolve, reject) => {
      this.worker.postMessage(["finish"]);
      this.worker.onmessage = e => {
        if (e.data) {
          resolve(e.data);
        } else {
          reject();
        }
      };
    });
  }

  /**
   * Requests access to the microphone and start recording
   * @return Promise
   */
  start(audioContext) {
    this.context = audioContext;
    this.worker.postMessage(["init", this.context.sampleRate]);

    const audio = this.config.deviceId
      ? { deviceId: { exact: this.config.deviceId } }
      : true;

    return new Promise((resolve, reject) => {
      navigator.mediaDevices
        .getUserMedia({ audio })
        .then(stream => {
          this.addMicrophoneListener(stream);
          resolve(stream);
        })
        .catch(function(err) {
          reject(err);
        });
    });
  }
}

export default MicRecorder;
